import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ClientProgressNav from '../../components/ui/ClientProgressNav';
import ExerciseHeader from './components/ExerciseHeader';
import SetTracker from './components/SetTracker';
import RepetitionInput from './components/RepetitionInput';
import IntensityAdjustment from './components/IntensityAdjustment';
import SupersetIndicator from './components/SupersetIndicator';
import SessionTimer from './components/SessionTimer';

const ExerciseExecution = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams?.get('session');
  const monitorMode = searchParams?.get('monitor') === 'true';

  // Mock exercise data
  const [currentExercise] = useState({
    id: 1,
    name: "Seated Leg Extensions",
    type: "dynamic",
    image: "https://images.pexels.com/photos/416809/pexels-photo-416809.jpeg",
    instructions: `Sit on the leg extension machine with your back against the backrest.\nAdjust the pad so it rests on your lower legs, just above your ankles.\nGrip the side handles for stability.\nSlowly extend your legs until they are straight.\nHold for a moment, then slowly lower back to starting position.`,
    targetMuscles: ["Quadriceps", "Knee Extensors"],
    isSuperset: true,
    supersetPartner: {
      id: 2,
      name: "Seated Calf Raises",
      type: "dynamic",
      targetReps: 12,
      weight: 15,
      unit: "lbs",
      intensity: 3
    }
  });

  const [sessionState, setSessionState] = useState({
    currentSet: 1,
    totalSets: 3,
    completedSets: 0,
    currentLoad: {
      weight: 20,
      unit: "lbs",
      targetReps: 10,
      intensity: 4
    },
    setResults: [],
    sessionTime: 0,
    isPaused: false,
    supersetPosition: 1 // A1, B1, A2, B2, A3, B3
  });

  const [showIntensityAdjustment, setShowIntensityAdjustment] = useState(false);
  const [pendingAdjustment, setPendingAdjustment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-save session state
  useEffect(() => {
    const saveSession = () => {
      localStorage.setItem('exerciseSession', JSON.stringify({
        ...sessionState,
        exerciseId: currentExercise?.id,
        timestamp: Date.now()
      }));
    };

    const interval = setInterval(saveSession, 30000); // Save every 30 seconds
    return () => clearInterval(interval);
  }, [sessionState, currentExercise?.id]);

  // Load saved session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('exerciseSession');
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        const timeDiff = Date.now() - parsed?.timestamp;
        
        // Allow resume within 4 hours (14400000 ms)
        if (timeDiff < 14400000) {
          setSessionState(prev => ({
            ...prev,
            ...parsed,
            sessionTime: parsed?.sessionTime + Math.floor(timeDiff / 1000)
          }));
        }
      } catch (error) {
        console.error('Failed to load saved session:', error);
      }
    }
  }, []);

  const calculateIntensityAdjustment = (completedReps, targetReps, currentIntensity) => {
    const performance = completedReps / targetReps;
    let adjustmentType = 'maintain';
    let newIntensity = currentIntensity;
    let newWeight = sessionState?.currentLoad?.weight;

    if (performance >= 1.2) { // 20% above target
      adjustmentType = 'increase';
      newIntensity = Math.min(currentIntensity + 1, 10);
      newWeight = sessionState?.currentLoad?.weight + 5;
    } else if (performance < 0.8) { // 20% below target
      adjustmentType = 'decrease';
      newIntensity = Math.max(currentIntensity - 1, 1);
      newWeight = Math.max(sessionState?.currentLoad?.weight - 5, 5);
    }

    return {
      type: adjustmentType,
      currentIntensity,
      newIntensity,
      currentWeight: sessionState?.currentLoad?.weight,
      newWeight,
      unit: sessionState?.currentLoad?.unit,
      reasoning: performance >= 1.2 
        ? "You exceeded the target significantly. Let's challenge you more!"
        : performance < 0.8 
        ? "Let's reduce the intensity to help you maintain proper form."
        : "You're performing perfectly at this intensity level."
    };
  };

  const handleRepetitionSubmit = (reps) => {
    setIsSubmitting(true);

    const newSetResult = {
      set: sessionState?.currentSet,
      reps,
      weight: sessionState?.currentLoad?.weight,
      intensity: sessionState?.currentLoad?.intensity,
      timestamp: Date.now()
    };

    // Update set results
    const updatedResults = [...sessionState?.setResults, newSetResult];
    
    setSessionState(prev => ({
      ...prev,
      setResults: updatedResults,
      completedSets: prev?.completedSets + 1
    }));

    // Check if this is the last set for intensity adjustment
    if (sessionState?.currentSet === sessionState?.totalSets) {
      const avgReps = updatedResults?.reduce((sum, result) => sum + result?.reps, 0) / updatedResults?.length;
      const adjustment = calculateIntensityAdjustment(
        avgReps, 
        sessionState?.currentLoad?.targetReps, 
        sessionState?.currentLoad?.intensity
      );

      if (adjustment?.type !== 'maintain') {
        setPendingAdjustment(adjustment);
        setShowIntensityAdjustment(true);
      } else {
        completeExercise();
      }
    } else {
      // Move to next set
      setTimeout(() => {
        setSessionState(prev => ({
          ...prev,
          currentSet: prev?.currentSet + 1
        }));
        setIsSubmitting(false);
      }, 1500);
    }
  };

  const handleIntensityConfirm = () => {
    if (pendingAdjustment) {
      setSessionState(prev => ({
        ...prev,
        currentLoad: {
          ...prev?.currentLoad,
          weight: pendingAdjustment?.newWeight,
          intensity: pendingAdjustment?.newIntensity
        }
      }));
    }
    
    setShowIntensityAdjustment(false);
    setPendingAdjustment(null);
    completeExercise();
  };

  const handleIntensityCancel = () => {
    setShowIntensityAdjustment(false);
    setPendingAdjustment(null);
    completeExercise();
  };

  const completeExercise = () => {
    setIsSubmitting(false);
    
    if (currentExercise?.isSuperset && sessionState?.supersetPosition < 6) {
      // Move to next superset exercise
      setSessionState(prev => ({
        ...prev,
        currentSet: 1,
        completedSets: 0,
        setResults: [],
        supersetPosition: prev?.supersetPosition + 1
      }));
    } else {
      // Exercise/superset complete - return to exercise list
      localStorage.removeItem('exerciseSession');
      navigate('/training-plan-selection');
    }
  };

  const handlePause = (paused) => {
    setSessionState(prev => ({
      ...prev,
      isPaused: paused
    }));
  };

  const handleEmergency = () => {
    // Save current state and navigate to help
    localStorage.setItem('emergencySession', JSON.stringify({
      ...sessionState,
      exerciseId: currentExercise?.id,
      timestamp: Date.now(),
      emergency: true
    }));
    navigate('/training-plan-selection');
  };

  const handleTimeUpdate = (newTime) => {
    setSessionState(prev => ({
      ...prev,
      sessionTime: newTime
    }));
  };

  const formatSessionTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const canGoBack = sessionState?.currentSet > 1 || sessionState?.supersetPosition > 1;
  const canGoForward = false; // Only allow forward after completing set

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - Exercise Info */}
          <div className="xl:col-span-2 space-y-6">
            <ExerciseHeader
              exerciseName={currentExercise?.name}
              currentSet={sessionState?.currentSet}
              totalSets={sessionState?.totalSets}
              currentLoad={sessionState?.currentLoad}
              exerciseType={currentExercise?.type}
              exerciseImage={currentExercise?.image}
              isSuperset={currentExercise?.isSuperset}
              supersetInfo={{
                current: Math.ceil(sessionState?.supersetPosition / 2),
                total: 3
              }}
            />

            {/* Superset Indicator */}
            {currentExercise?.isSuperset && (
              <SupersetIndicator
                currentExercise={currentExercise}
                nextExercise={currentExercise?.supersetPartner}
                supersetPosition={sessionState?.supersetPosition}
                totalSupersetExercises={2}
                onNextExercise={() => {}}
              />
            )}

            {/* Set Tracker */}
            <SetTracker
              completedSets={sessionState?.completedSets}
              totalSets={sessionState?.totalSets}
              currentSet={sessionState?.currentSet}
              setResults={sessionState?.setResults}
            />
          </div>

          {/* Right Column - Input & Controls */}
          <div className="space-y-6">
            {/* Session Timer */}
            <SessionTimer
              isActive={!sessionState?.isPaused}
              isPaused={sessionState?.isPaused}
              onTimeUpdate={handleTimeUpdate}
              initialTime={sessionState?.sessionTime}
            />

            {/* Repetition Input */}
            <RepetitionInput
              onRepetitionSubmit={handleRepetitionSubmit}
              targetReps={sessionState?.currentLoad?.targetReps}
              isLoading={isSubmitting}
              currentSet={sessionState?.currentSet}
              disabled={sessionState?.isPaused}
            />
          </div>
        </div>
      </div>
      {/* Intensity Adjustment Modal */}
      <IntensityAdjustment
        adjustment={pendingAdjustment}
        onConfirm={handleIntensityConfirm}
        onCancel={handleIntensityCancel}
        isVisible={showIntensityAdjustment}
      />
      {/* Bottom Navigation */}
      <ClientProgressNav
        currentStep={sessionState?.supersetPosition}
        totalSteps={6}
        exerciseName={currentExercise?.name}
        sessionTime={formatSessionTime(sessionState?.sessionTime)}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={() => {}}
        onForward={() => {}}
        onPause={handlePause}
        onEmergency={handleEmergency}
      />
    </div>
  );
};

export default ExerciseExecution;